import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { OutlineButton } from "@/components/Primitives";
import { OCC_COLORS } from "@/components/property/common";
import { OverviewTab } from "@/components/property/OverviewTab";
import { FinancialsTab } from "@/components/property/FinancialsTab";
import { MortgageTab } from "@/components/property/MortgageTab";
import { ManagerTab } from "@/components/property/ManagerTab";
import { InsuranceTab } from "@/components/property/InsuranceTab";
import { MaintenanceTab } from "@/components/property/MaintenanceTab";
import { DocumentsTab } from "@/components/property/DocumentsTab";
import { CalendarTab } from "@/components/property/CalendarTab";
import { ReportsTab, SettingsTab } from "@/components/property/MiscTabs";
import {
  LayoutGrid, Wallet, Landmark, UserCog, ShieldCheck, Wrench, FileText,
  CalendarDays, FileBarChart, Settings as SettingsIcon, ArrowLeft, ImagePlus, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "financials", label: "Financials", icon: Wallet },
  { id: "mortgage", label: "Mortgage", icon: Landmark },
  { id: "manager", label: "Property Manager", icon: UserCog },
  { id: "insurance", label: "Insurance", icon: ShieldCheck },
  { id: "maintenance", label: "Maintenance", icon: Wrench },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "reports", label: "Reports", icon: FileBarChart },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

export default function PropertyWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cur = user?.currency || "AUD";
  const [prop, setProp] = useState(null);
  const [options, setOptions] = useState({});
  const [tab, setTab] = useState("overview");
  const [uploading, setUploading] = useState(false);
  const photoRef = useRef();
  const docRef = useRef();
  const pendingCat = useRef("Other");

  const reload = () => api.get(`/properties/${id}`).then((r) => setProp(r.data)).catch(() => navigate("/properties"));
  useEffect(() => {
    reload();
    api.get("/properties/meta/options").then((r) => setOptions(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, [id]);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/properties/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo updated"); reload(); }
    catch { toast.error("Could not upload photo"); } finally { setUploading(false); if (photoRef.current) photoRef.current.value = ""; }
  };

  // uploader for Mortgage/Insurance "Upload" buttons (category-tagged document)
  const onUpload = (category) => { pendingCat.current = category || "Other"; docRef.current?.click(); };
  const onDocFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("category", pendingCat.current);
    try { await api.post(`/properties/${id}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document filed under " + pendingCat.current); }
    catch { toast.error("Could not upload"); } finally { if (docRef.current) docRef.current.value = ""; }
  };

  if (!prop) return <div className="text-[#8A7F70] text-sm">Loading…</div>;

  const tabProps = { property: prop, currency: cur, options, reload };

  return (
    <div data-testid="property-workspace">
      <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="property-photo-input" />
      <input ref={docRef} type="file" className="hidden" onChange={onDocFile} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 fade-up">
        <div className="flex items-center gap-3 min-w-0">
          <button data-testid="property-back" onClick={() => navigate("/properties")} className="w-9 h-9 rounded-full bg-white/70 border border-[#EAE0D2] flex items-center justify-center text-[#6b6256] hover:border-[#C6A45C] shrink-0"><ArrowLeft size={16} /></button>
          <div className="min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl font-light text-[#2C2A28] truncate">{prop.nickname}</h1>
            <div className="text-sm text-[#8A7F70] truncate">{prop.address || "No address yet"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge style={{ background: `${OCC_COLORS[prop.occupancy_status] || "#B79B86"}22`, color: OCC_COLORS[prop.occupancy_status] || "#6b6256", borderColor: `${OCC_COLORS[prop.occupancy_status] || "#B79B86"}55` }}>{prop.occupancy_status}</Badge>
          <OutlineButton data-testid="property-photo-btn" onClick={() => photoRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Photo
          </OutlineButton>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Desktop left nav / Mobile scrollable top tabs */}
        <nav className="lg:w-56 shrink-0">
          <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 -mx-1 px-1" data-testid="property-subnav">
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button key={t.id} data-testid={`ptab-${t.id}`} onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm whitespace-nowrap transition-all min-h-[44px] ${active ? "bg-[#C6A45C]/15 text-[#8a6d2f] font-medium" : "text-[#6b6256] hover:bg-white/70"}`}>
                  <t.icon size={17} strokeWidth={1.6} /><span>{t.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        <div className="flex-1 min-w-0 fade-up">
          {tab === "overview" && <OverviewTab property={prop} currency={cur} />}
          {tab === "financials" && <FinancialsTab {...tabProps} />}
          {tab === "mortgage" && <MortgageTab property={prop} reload={reload} onUpload={onUpload} />}
          {tab === "manager" && <ManagerTab property={prop} reload={reload} />}
          {tab === "insurance" && <InsuranceTab property={prop} reload={reload} />}
          {tab === "maintenance" && <MaintenanceTab {...tabProps} />}
          {tab === "documents" && <DocumentsTab property={prop} options={options} />}
          {tab === "calendar" && <CalendarTab property={prop} options={options} />}
          {tab === "reports" && <ReportsTab />}
          {tab === "settings" && <SettingsTab property={prop} options={options} reload={reload} onDeleted={() => navigate("/properties")} />}
        </div>
      </div>
    </div>
  );
}
